import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getInvestorDeckByToken } from "@/lib/investor-leads.functions";

export const Route = createFileRoute("/investor-deck/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "ClutchCube Investor Deck" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DeckViewer,
});

function DeckViewer() {
  const { token } = Route.useParams();
  const fetchDeck = useServerFn(getInvestorDeckByToken);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ok"; name: string; deckUrl: string; filename: string }
    | { status: "invalid" }
    | { status: "error"; message: string }
  >({ status: "loading" });

  useEffect(() => {
    let alive = true;
    fetchDeck({ data: { token } })
      .then((res) => {
        if (!alive) return;
        if (res.ok) {
          setState({ status: "ok", name: res.name, deckUrl: res.deckUrl, filename: res.filename });
        } else {
          setState({ status: "invalid" });
        }
      })
      .catch((e) => alive && setState({ status: "error", message: (e as Error).message }));
    return () => {
      alive = false;
    };
  }, [token, fetchDeck]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-arena flex items-center justify-center text-muted-foreground">
        Loading your deck…
      </div>
    );
  }

  if (state.status === "invalid" || state.status === "error") {
    return (
      <div className="min-h-screen bg-arena flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-card border border-border rounded-sm p-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Invalid Link</p>
          <h1 className="font-display text-3xl mb-3">This deck link isn't valid</h1>
          <p className="text-sm text-muted-foreground">
            The link may have expired or been mistyped. Please request a fresh copy from the ClutchCube team.
          </p>
          <a
            href="/#invest"
            className="mt-6 inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-xs rounded-sm"
          >
            Request Access
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arena flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/80 backdrop-blur">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-primary">ClutchCube · Confidential</p>
          <p className="text-sm text-muted-foreground">Prepared for {state.name}</p>
        </div>
        <a
          href={state.deckUrl}
          download={state.filename}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-5 py-2.5 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-[11px] rounded-sm shadow-glow hover:translate-y-[-1px] transition"
        >
          Download PDF
        </a>
      </header>
      <div className="flex-1">
        <object data={state.deckUrl} type="application/pdf" className="w-full h-[calc(100vh-4.5rem)] bg-background">
          <iframe
            src={state.deckUrl}
            title="ClutchCube Investor Deck"
            className="w-full h-[calc(100vh-4.5rem)] border-0 bg-background"
          />
          <div className="flex flex-col items-center justify-center gap-4 py-20 text-center px-6">
            <p className="text-sm text-muted-foreground">
              Your browser can't display the deck inline.
            </p>
            <a
              href={state.deckUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-xs rounded-sm"
            >
              Open the Investor Deck
            </a>
          </div>
        </object>
      </div>
    </div>
  );
}
